define('headerChatView',[
  'app',
  'module',
  'marionette',
  'underscore',
  'notificationCenter',
  'template!headerChatView',
  // Side-effect import: registers the `chatInbox:*` reqres handlers
  // (including chatInbox:counts). The header is on every page, so we
  // can't rely on the chat-inbox page module having loaded it.
  'entities/chat_inbox'
], function (
  App,
  Module,
  Marionette,
  _,
  NotificationCenter,
  Tpl
) {
  'use strict';

  var SOUND_MUTED_KEY = 'compuzz-chat-sound-muted';
  // Reminder chime cadence while at least one chat is still unclaimed.
  var UNASSIGNED_RING_INTERVAL_MS = 10000;

  Module.exports = Marionette.LayoutView.extend({
    template: Tpl,
    className: 'header-chat-view',

    ui: {
      content: '> .content',
      countUnassigned: '.count-unassigned',
      countMine: '.count-mine',
      muteToggle: '.mute-toggle'
    },

    events: {
      'click @ui.content': 'onOpen',
      'click @ui.muteToggle': 'onToggleMute'
    },

    initialize: function () {
      this.unassigned = 0;
      this.mine = 0;
      // Sound-muted preference is per browser, persisted so it sticks.
      this.soundMuted = false;
      try {
        this.soundMuted = window.localStorage.getItem(SOUND_MUTED_KEY) === '1';
      } catch (e) { /* private mode / storage disabled — default unmuted */ }
      // Leading-edge debounce: a burst of messages plays the chime once,
      // then stays quiet for 2s.
      this.playSoundDebounced = _.debounce(_.bind(this.playSound, this), 2000, true);
      this.unassignedRingTimer = null;
    },

    onShow: function () {
      this.subscribeToNotifications();
      this.fetchCounts(true);
      this.startUnassignedRing();
    },

    onRender: function () {
      this.renderCounts();
      this.renderMuteState();
    },

    /**
     * Re-fetch on every chat lifecycle WS event so the badges track
     * reality without waiting for the 30s poll. Debounced because one
     * action can fan out several events (a claim fires CHAT_SESSION_CLAIMED
     * to the whole group) and we don't want N requests for one change.
     * addListener() auto-unsubscribes when this view is destroyed.
     */
    subscribeToNotifications: function () {
      var nc = NotificationCenter.getInstance();
      var refresh = _.debounce(_.bind(function () {
        this.fetchCounts(false);
      }, this), 400);
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_STARTED, refresh);
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_CLAIMED, refresh);
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_RELEASED, refresh);
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_RESOLVED, refresh);
      // CHAT_MESSAGE_ADDED gets its own handler: a message on an *existing*
      // chat doesn't move the unassigned/mine counts, so a silent re-fetch
      // would be invisible. We still re-fetch (a first message can create a
      // session and bump a count) but ALSO pulse the icon so the agent sees
      // that a new message landed.
      nc.addListener(this, NotificationCenter.events.CHAT_MESSAGE_ADDED,
        _.bind(this.onChatMessage, this));
    },

    onChatMessage: function (message) {
      this.fetchCounts(false);
      // Only react to visitor-typed messages — not the agent's own replies
      // or AI/system messages. The backend already only routes
      // CHAT_MESSAGE_ADDED to relevant recipients (group members while
      // unclaimed, the assignee once claimed), so any message reaching us
      // is one this agent should notice.
      var content = message && message.content && message.content.content;
      var data = content && content.data;
      if (data && data.sender === 'CUSTOMER') {
        this.pulse();
        if (!this.soundMuted) {
          this.playSoundDebounced();
        }
      }
    },

    /**
     * Short two-note chime synthesised with the Web Audio API — avoids
     * shipping a binary audio asset. A fresh AudioContext per play, closed
     * shortly after, keeps things simple; at a few messages an hour the
     * overhead is nil. Browsers gate audio on prior user interaction, but
     * the agent has been clicking around the dashboard long before any
     * message arrives, so play() is allowed.
     */
    playSound: function () {
      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) {
        return;
      }
      var ctx;
      try {
        ctx = new Ctx();
      } catch (e) {
        return;
      }
      var now = ctx.currentTime;
      var notes = [ { f: 880, t: 0 }, { f: 1174.66, t: 0.13 } ];
      notes.forEach(function (note) {
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = note.f;
        osc.connect(gain);
        gain.connect(ctx.destination);
        var start = now + note.t;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.22, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.4);
        osc.start(start);
        osc.stop(start + 0.45);
      });
      setTimeout(function () {
        try { ctx.close(); } catch (e) { /* already closed */ }
      }, 900);
    },

    /**
     * Periodic reminder chime so an idle agent is nagged until every
     * waiting chat has been claimed. Runs every 10s for the lifetime of
     * the header view; each tick is a no-op when the queue is empty or
     * sound is muted, so the timer stays cheap when nothing's waiting.
     * The message-triggered chime in onChatMessage is unchanged — this
     * is purely the "still waiting" reminder.
     */
    startUnassignedRing: function () {
      if (this.unassignedRingTimer) {
        return;
      }
      this.unassignedRingTimer = setInterval(
        _.bind(this.onUnassignedRingTick, this),
        UNASSIGNED_RING_INTERVAL_MS
      );
    },

    onUnassignedRingTick: function () {
      if (this.isDestroyed) {
        return;
      }
      // Mute silences both the chime and the notifications — it's the
      // single "do not disturb" switch for this reminder.
      if (this.unassigned > 0 && !this.soundMuted) {
        this.playSound();
        this.notifyUnassigned();
      }
    },

    onToggleMute: function (e) {
      // The toggle sits inside .content (whose click navigates to the chat
      // inbox) — stop the bubble so muting doesn't also navigate.
      if (e && e.stopPropagation) {
        e.stopPropagation();
      }
      this.soundMuted = !this.soundMuted;
      try {
        window.localStorage.setItem(SOUND_MUTED_KEY, this.soundMuted ? '1' : '0');
      } catch (ex) { /* storage disabled — preference just won't persist */ }
      this.renderMuteState();
    },

    renderMuteState: function () {
      if (this.isDestroyed || !this.ui.muteToggle || !this.ui.muteToggle.toggleClass) {
        return;
      }
      this.ui.muteToggle.toggleClass('muted', this.soundMuted);
      this.ui.muteToggle.attr('title', this.soundMuted ?
        'Chat sound off — click to turn on' :
        'Chat sound on — click to mute');
    },

    /**
     * Brief attention pulse on the icon. Remove-then-re-add (with a tiny
     * defer) restarts the CSS animation cleanly when several messages
     * arrive in a row; the class is cleared once the animation finishes.
     */
    pulse: function () {
      if (this.isDestroyed || !this.ui.content || !this.ui.content.addClass) {
        return;
      }
      var self = this;
      this.ui.content.removeClass('pulse');
      setTimeout(function () {
        if (self.isDestroyed || !self.ui.content || !self.ui.content.addClass) {
          return;
        }
        self.ui.content.addClass('pulse');
        setTimeout(function () {
          if (!self.isDestroyed && self.ui.content && self.ui.content.removeClass) {
            self.ui.content.removeClass('pulse');
          }
        }, 700);
      }, 20);
    },

    fetchCounts: function (timer) {
      if (this.isDestroyed) {
        return;
      }
      App.request('chatInbox:counts').done(_.bind(function (counts) {
        if (this.isDestroyed) {
          return;
        }
        var previousUnassigned = this.unassigned;
        this.unassigned = (counts && counts.unassigned) || 0;
        this.mine = (counts && counts.mine) || 0;
        this.renderCounts();
        // Surface an in-page (or system) notification when fresh
        // unclaimed work appears. Gated on "count grew" so claiming or
        // resolving a chat doesn't re-trigger it, and on the mute
        // toggle so a muted agent stays undisturbed.
        if (this.unassigned > previousUnassigned && !this.soundMuted) {
          this.notifyUnassigned();
        }
      }, this));

      if (timer) {
        setTimeout(_.bind(this.fetchCounts, this, true), 30000);
      }
    },

    /**
     * Always show the in-page $.notify toast so the agent sees a
     * notification inside the dashboard regardless of OS permission.
     * Additionally fire a system Web Notification when the browser
     * allows it, so an agent on another tab still gets the popup. The
     * tag collapses repeat system notifications onto a single one so
     * back-to-back triggers don't fan out a stack.
     */
    notifyUnassigned: function () {
      var count = this.unassigned;
      var title = count === 1 ?
        'Unclaimed chat waiting' :
        count + ' unclaimed chats waiting';
      var body = count === 1 ?
        'A customer is waiting to be claimed — click to open.' :
        count + ' customers are waiting to be claimed — click to open.';
      // forceJsNotification=true routes the helper to $.notify, so the
      // in-page toast fires unconditionally.
      NotificationCenter.getInstance().displayNotification(
        title,
        body,
        '#chat-inbox',
        8000,
        'warning',
        true,
        'compuzz-chat-unassigned'
      );
      if (window.Notification && window.Notification.permission === 'granted') {
        try {
          var fullUrl = document.location.origin +
            document.location.pathname + '#chat-inbox';
          var notif = new window.Notification(title, {
            body: body,
            tag: 'compuzz-chat-unassigned'
          });
          notif.onclick = function () {
            window.location = fullUrl;
            window.focus();
          };
          window.setTimeout(function () {
            try { notif.close(); } catch (e) { /* already closed */ }
          }, 8000);
        } catch (e) { /* notifications not supported in this context */ }
      }
    },

    renderCounts: function () {
      if (this.isDestroyed) {
        return;
      }
      this.renderBadge(this.ui.countUnassigned, this.unassigned);
      this.renderBadge(this.ui.countMine, this.mine);
      if (this.ui.content && this.ui.content.toggleClass) {
        this.ui.content.toggleClass('activated', (this.unassigned + this.mine) > 0);
        // Continuous attention cue while at least one chat is unclaimed;
        // pairs with the 10s reminder chime.
        this.ui.content.toggleClass('has-unassigned', this.unassigned > 0);
      }
    },

    renderBadge: function ($el, value) {
      if (!$el || !$el.html) {
        return;
      }
      if (value > 0) {
        $el.html(value > 99 ? '99+' : value);
        $el.css('display', 'flex');
      } else {
        $el.hide();
      }
    },

    onOpen: function () {
      App.navigate('chat-inbox', { trigger: true });
    },

    onDestroy: function () {
      if (this.unassignedRingTimer) {
        clearInterval(this.unassignedRingTimer);
        this.unassignedRingTimer = null;
      }
    }
  });
});

